import {CSSProperties} from 'styled-components';

import {OptionalOrUndefinedKeys} from '@shared/lib/utils/type_utils';

import {optional, optionalPx} from '@shared_web/lib/styled_utils';

export type SizeToken = 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl';
export type CssSize = number | SizeToken;

export interface CommonCssProps {
  // Raw values
  $width?: CSSProperties['width'];
  $height?: CSSProperties['height'];
  $backgroundColor?: CSSProperties['backgroundColor'];
  $color?: CSSProperties['color'];
  $overflow?: CSSProperties['overflow'];
  $overflowX?: CSSProperties['overflowX'];
  $overflowY?: CSSProperties['overflowY'];
  // CssSize values
  $padding?: CssSize | CssSize[];
  $margin?: CssSize | CssSize[];
  $radius?: CssSize;
}

const sizeTokenToPixels: Record<SizeToken, number> = {
  xs: 4, // 2 * 2
  s: 8, // 2 * 2 * 2
  m: 12, // 2 * 2 * 3
  l: 16, // 2 * 2 * 2 * 2
  xl: 24, // 2 * 2 * 2 * 3
  xxl: 32, // 2 * 2 * 2 * 2 * 2
};

export function sizeToPx(size?: CssSize): number | undefined {
  if (size === undefined) {
    return undefined;
  }
  if (typeof size === 'string') {
    return sizeTokenToPixels[size as SizeToken];
  }
  return size;
}

const cssRadius = (size?: CssSize) => {
  return optionalPx('border-radius', sizeToPx(size));
};

const cssPaddings = (size?: CssSize | CssSize[]) => cssDirections('padding', size);
const cssMargins = (size?: CssSize | CssSize[]) => cssDirections('margin', size);

function cssDirections(cssPrefix: string, size?: CssSize | CssSize[]) {
  if (size === undefined) {
    return false;
  }
  if (!Array.isArray(size)) {
    return optionalPx(cssPrefix, sizeToPx(size));
  }
  const [s1, s2, s3, s4] = size;
  const hasS1 = s1 !== undefined;
  const hasS2 = s2 !== undefined;
  const hasS3 = s3 !== undefined;
  const hasS4 = s4 !== undefined;
  if (hasS1 && hasS2 && hasS3 && hasS4) {
    return [
      optionalPx(`${cssPrefix}-top`, sizeToPx(s1)),
      optionalPx(`${cssPrefix}-right`, sizeToPx(s2)),
      optionalPx(`${cssPrefix}-bottom`, sizeToPx(s3)),
      optionalPx(`${cssPrefix}-left`, sizeToPx(s4)),
    ].join(' ');
  }
  if (hasS1 && hasS2 && hasS3) {
    return [
      optionalPx(`${cssPrefix}-top`, sizeToPx(s1)),
      optionalPx(`${cssPrefix}-right`, sizeToPx(s2)),
      optionalPx(`${cssPrefix}-bottom`, sizeToPx(s3)),
    ].join(' ');
  }
  if (hasS1 && hasS2) {
    return [
      optionalPx(`${cssPrefix}-top`, sizeToPx(s1)),
      optionalPx(`${cssPrefix}-bottom`, sizeToPx(s1)),
      optionalPx(`${cssPrefix}-right`, sizeToPx(s2)),
      optionalPx(`${cssPrefix}-left`, sizeToPx(s2)),
    ].join(' ');
  }
  if (hasS1) {
    return optionalPx(cssPrefix, sizeToPx(s1));
  }
  return false;
}

export const applyCommonCss = (props: CommonCssProps) => {
  return [
    // CssSize values
    cssRadius(props.$radius),
    cssPaddings(props.$padding),
    cssMargins(props.$margin),
    // Raw values
    optionalPx('width', props.$width),
    optionalPx('height', props.$height),
    props.$backgroundColor && `background-color: ${props.$backgroundColor};`,
    props.$color && `color: ${props.$color};`,
    optional('overflow', props.$overflow),
    optional('overflow-x', props.$overflowX),
    optional('overflow-y', props.$overflowY),
  ]
    .filter(Boolean)
    .join('\n');
};

// Extract CommonCssProps from provided props and return them separated from the rest
export function extractCssProps<T extends CommonCssProps>(
  props: T
): {cssProps: CommonCssProps; rest: Omit<T, keyof CommonCssProps>} {
  const {
    // Raw values
    $width,
    $height,
    $backgroundColor,
    $color,
    $overflow,
    $overflowX,
    $overflowY,
    // CssSize values
    $padding,
    $margin,
    $radius,
    // non-CommonCssProps
    ...rest
  } = props;

  const cssProps: {
    [K in OptionalOrUndefinedKeys<CommonCssProps>]: CommonCssProps[K];
  } = {
    $width,
    $height,
    $backgroundColor,
    $color,
    $overflow,
    $overflowX,
    $overflowY,
    $padding,
    $margin,
    $radius,
  };

  return {cssProps, rest};
}
